import { mergeAttributes } from "@tiptap/core";
import { Mention, type MentionOptions } from "@tiptap/extension-mention";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { EntityData, EntityKind } from "#app/lib/entity-visuals";
import { EntityChip } from "./entity-chip";
import {
  KNOWN_MENTION_KINDS,
  mentionTriggerChar,
  normalizeMentionKind,
  type EntityMentionRef,
} from "./mention-refs";
import { createSuggestionRenderer } from "./suggestion-list";
import { MentionMenu } from "./mention-menu";
import { omitUndefined, type Omittable } from "@salestrak/ui";

// The kind is serialized into `data-type` (not a generic `entityMention`)
// because `NotesService.extractMentions` keys notifications on `data-type`.
export type { EntityKind } from "#app/lib/entity-visuals";
export type { EntityMentionRef } from "./mention-refs";

export type MentionInsert = Omittable<{
  id: string;
  label: string;
  entity: EntityKind;
  workflowId?: string | null;
}>;

// `addOptions()` writes `resolveEntity: undefined` as the extension's default,
// so the member relaxes — an unset resolver and an absent key are the same
// "fall back to the persisted attributes".
export type EntityMentionOptions = MentionOptions &
  Omittable<{
    // Returning undefined makes the chip fall back to EntityData built from the
    // persisted {kind,id,label}.
    resolveEntity?: (ref: EntityMentionRef) => EntityData | undefined;
  }>;

export const EntityMention = Mention.extend<EntityMentionOptions>({
  name: "entityMention",

  addOptions() {
    return {
      ...this.parent?.(),
      resolveEntity: undefined,
    } as EntityMentionOptions;
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-id"),
        renderHTML: (attrs) => (attrs.id ? { "data-id": attrs.id } : {}),
      },
      label: {
        default: null,
        parseHTML: (el) =>
          el.getAttribute("data-label") ??
          el.textContent?.replace(/^[@#]/, "") ??
          null,
        renderHTML: (attrs) => (attrs.label ? { "data-label": attrs.label } : {}),
      },
      entity: {
        default: "user",
        parseHTML: (el) => normalizeMentionKind(el.getAttribute("data-type")),
        renderHTML: (attrs) => ({ "data-type": (attrs.entity as string) ?? "user" }),
      },
      workflowId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-workflow-id"),
        renderHTML: (attrs) =>
          attrs.workflowId ? { "data-workflow-id": attrs.workflowId } : {},
      },
    };
  },

  parseHTML() {
    // A span is a mention only with a recognized data-type (or legacy "mention")
    // AND a data-id — so pasted external `span[data-id]` markup stays plain text.
    return [
      {
        tag: "span[data-type]",
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          const dt = el.getAttribute("data-type");
          if (!dt) return false;
          if (
            dt !== "mention" &&
            !(KNOWN_MENTION_KINDS as readonly string[]).includes(dt)
          )
            return false;
          if (!el.getAttribute("data-id")) return false;
          return null;
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = (node.attrs.label as string) ?? (node.attrs.id as string) ?? "";
    const kind = (node.attrs.entity as EntityKind) ?? "user";
    return [
      "span",
      mergeAttributes({ class: "mention" }, this.options.HTMLAttributes, HTMLAttributes),
      `${mentionTriggerChar(kind)}${label}`,
    ];
  },

  renderText({ node }) {
    const label = (node.attrs.label as string) ?? (node.attrs.id as string) ?? "";
    const kind = (node.attrs.entity as EntityKind) ?? "user";
    return `${mentionTriggerChar(kind)}${label}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(EntityChip);
  },
});

export interface EntitySuggestionSource {
  char: string;
  kind: EntityKind;
  pluginKey: SuggestionOptions["pluginKey"];
}

export function buildEntitySuggestion(
  source: EntitySuggestionSource,
): Omit<SuggestionOptions, "editor"> {
  // Annotated `Omittable<…>` so the callbacks below stay contextually typed,
  // then `omitUndefined` because a source with no plugin key must DELETE it —
  // TipTap's suggestion options predate `exactOptionalPropertyTypes`.
  const options: Omittable<Omit<SuggestionOptions, "editor">> = {
    char: source.char,
    pluginKey: source.pluginKey,
    // Allow single spaces so multi-word entities ("@John Smith") match as one
    // query; a double space deactivates the suggestion, ending the mention.
    allowSpaces: true,
    allow: ({ state, range }) => {
      const text = state.doc.textBetween(range.from, range.to, undefined, " ");
      return !text.includes("  ");
    },
    // Data comes from the pool inside MentionMenu; [] keeps the match active.
    items: () => [],
    command: ({ editor, range, props }) => {
      const item = props as MentionInsert;
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: "entityMention",
            attrs: {
              id: item.id,
              label: item.label,
              entity: item.entity,
              workflowId: item.workflowId ?? null,
            },
          },
          { type: "text", text: " " },
        ])
        .run();
    },
    render: createSuggestionRenderer({
      component: MentionMenu,
      mapProps: (props) => ({
        kind: source.kind,
        query: props.query,
        command: (item: MentionInsert) => props.command(item),
      }),
    }),
  };
  return omitUndefined(options);
}
